import { useState } from 'react';
import {
  Rocket,
  Globe,
  Check,
  Heart,
  Zap,
  Users,
  Award,
  Shield,
  TrendingUp,
  Sparkles,
  Mail,
  Building,
  ArrowRight,
  ArrowUpRight,
  Calendar,
  Gift,
  Target,
  ChevronDown,
  BookOpen,
} from 'lucide-react';
import SEO from '@/components/SEO';
import { site } from '@/data/content';

const whySupport = [
  { icon: Heart, title: 'We were a startup too', text: 'ZuriHub started as a lean team with limited tooling. We know how hard it is to build a support stack on a tight budget.' },
  { icon: Globe, title: 'Global by default', text: 'Startups in Africa, Asia, Europe, and the Americas use the same reliable, low-latency platform from day one.' },
  { icon: TrendingUp, title: 'Growth should not break support', text: 'Founders should spend time on product and customers, not wiring together five support tools.' },
];

const eligibility = [
  'Incorporated startup, non-profit, or social enterprise under 5 years old',
  'Fewer than 50 employees or an annual revenue under $1M USD',
  'A public website, pitch deck, or app store listing we can verify',
  'Committed to responding to customers within 48 hours',
];

const offering = [
  { icon: Gift, title: '$1,500 ZuriHub credit', text: 'Free credit for 12 months to cover your Pro agent seats, channels, and AI usage.' },
  { icon: Users, title: 'Up to 5 agents included', text: 'Invite your founding team, support lead, and operations without per-seat cost for a year.' },
  { icon: Rocket, title: 'Fast-track onboarding', text: 'A 60-minute setup session to connect WhatsApp, live chat, email, and your knowledge base.' },
  { icon: Award, title: 'Priority support', text: 'Direct access to our startup success team via chat and email during your first year.' },
];

const multiYearDiscounts = [
  { year: 'Year 2', discount: '50%', seats: '5', description: '50% discount on Pro plan for up to 5 agent seats' },
  { year: 'Year 3', discount: '25%', seats: '5', description: '25% discount on Pro plan for up to 5 agent seats' },
  { year: 'Year 4', discount: '10%', seats: '5', description: '10% discount on Pro plan for up to 5 agent seats' },
  { year: 'Year 5', discount: '5%', seats: '5', description: '5% discount on Pro plan for up to 5 agent seats' },
];

const additionalBenefits = [
  { icon: Calendar, title: 'Free Startup Events Access', text: 'Complimentary access to exclusive startup networking events and workshops.' },
  { icon: Sparkles, title: 'ZuriConnect at 50% Off', text: 'Attend ZuriConnect conferences at 50% discounted rate for startup founders.' },
  { icon: Globe, title: 'Social Media Mentions', text: 'Get featured on ZuriHub social media channels and startup success stories.' },
  { icon: Building, title: 'Website Showcasing', text: 'Your startup showcased on ZuriHub website with backlink to improve SEO.' },
  { icon: Users, title: 'Dedicated Success Coach', text: 'Personal startup success coach to guide your customer experience strategy.' },
  { icon: BookOpen, title: 'Free Resources Access', text: 'Access to premium templates, guides, and best practices library.' },
];

const whyChoose = [
  { icon: Zap, title: 'All channels in one inbox', text: 'WhatsApp, Instagram, Telegram, X, Messenger, Gmail, and live chat in one thread.' },
  { icon: Sparkles, title: 'Zyra AI from day one', text: 'Draft replies, summarize threads, and deflect common questions without a data-science team.' },
  { icon: Shield, title: 'Enterprise-grade reliability', text: '99.99% uptime SLA, encryption, and audit logs so you look bigger than you are.' },
  { icon: Target, title: 'Exclusive multi-year discounts', text: 'Year 2 (50%), Year 3 (25%), Year 4 (10%), Year 5 (5%) on Pro plan for up to 5 seats.' },
];

const whatToExpect = [
  { step: '1', title: 'Apply in 5 minutes', text: 'Share your startup details and why you need ZuriHub. The application is reviewed within 3 business days.' },
  { step: '2', title: 'Get approved and onboard', text: 'Approved startups receive workspace credit and a setup call to connect channels.' },
  { step: '3', title: 'Serve customers for 12 months free', text: 'Use Pro features, AI, and omnichannel tools for one year. Renew on friendly pricing after that.' },
];

const startupLogos = [
  { name: 'Pumzi Teas', category: 'Consumer & Wellness' },
  { name: 'Juliro Agro Ecology', category: 'Climate & Agriculture' },
  { name: 'JCCI', category: 'Business Services' },
  { name: 'Chamix Tech', category: 'Technology' },
  { name: 'Moove Africa', category: 'Logistics & Mobility' },
];

const faqs = [
  { q: 'Is the startup credit really free for a full year?', a: 'Yes. Eligible startups receive $1,500 in ZuriHub credit that covers Pro agent seats, channels, and AI usage for up to 12 months.' },
  { q: 'What are the multi-year startup discounts?', a: 'Year 2: 50% discount for up to 5 seats. Year 3: 25% discount. Year 4: 10% discount. Year 5: 5% discount. These exclusive rates apply to startup program members only.' },
  { q: 'Can any country apply?', a: 'Yes. The program is open to startups globally, as long as they can receive services and payments in their region or use the platform online.' },
  { q: 'What startup events are included?', a: 'Free access to exclusive startup networking events, workshops, and ZuriConnect conferences at 50% discounted rate. Plus social media mentions and website showcasing.' },
  { q: 'Do I need a technical co-founder to set up ZuriHub?', a: 'No. Most startup teams connect their first channel and widget in under an hour. We also include a free onboarding call and dedicated success coach.' },
];

const jsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'ZuriHub for Startups',
    description: 'Free ZuriHub credit for one year for eligible startups around the world. Omnichannel support, AI, and ticketing for growing teams.',
    url: `${site.baseUrl}/startup`,
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${site.baseUrl}/startup` },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to apply for ZuriHub Startup credit',
    step: whatToExpect.map((s) => ({ '@type': 'HowToStep', name: s.title, text: s.text })),
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({ '@type': 'Question', name: f.q, acceptedAnswer: { '@type': 'Answer', text: f.a } })),
  },
];

export default function Startup() {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    website: '',
    country: '',
    stage: '',
    team: '',
    why: '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = `Startup program application from ${form.company || form.name}`;
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Company: ${form.company}`,
      `Website: ${form.website || 'Not provided'}`,
      `Country: ${form.country}`,
      `Stage: ${form.stage || 'Not provided'}`,
      `Team size: ${form.team || 'Not provided'}`,
      '',
      'Why ZuriHub:',
      form.why || 'No details provided',
    ].join('\n');
    window.location.href = `mailto:startup@getzurihub.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  return (
    <>
      <SEO
        title="ZuriHub for Startups - Free Credit + Multi-Year Discounts"
        description="Get $1,500 free ZuriHub credit for one year plus exclusive multi-year discounts: Year 2 (50%), Year 3 (25%), Year 4 (10%), Year 5 (5%). Free startup events, ZuriConnect access, social media mentions, and dedicated success coach."
        path="/startup"
        jsonLd={jsonLd}
      >
        <meta
          name="keywords"
          content="startup customer support, free help desk for startups, ZuriHub startup program, startup credit, WhatsApp for startups, AI support for startups, GovTech, social enterprise support, startup discounts, multi-year pricing"
        />
      </SEO>

      <section className="relative overflow-hidden bg-slate-950 px-4 pt-28 pb-20 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-4xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-4 py-1.5 text-sm font-medium text-slate-300 backdrop-blur">
            <Rocket className="h-4 w-4" /> Startup Program
          </span>
          <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            Free support tools for <span className="gradient-text">1 full year</span>
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-lg text-slate-300">
            Eligible startups around the world get $1,500 in ZuriHub credit, onboarding, priority support, and exclusive multi-year discounts. No pitch competition. No strings attached.
          </p>
          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <a
              href="#apply"
              className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-7 py-3.5 text-base font-bold text-white transition hover:bg-brand-700"
            >
              Apply now <ChevronDown className="h-4 w-4" />
            </a>
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-900/60 px-7 py-3.5 text-base font-semibold text-white transition hover:bg-slate-800"
            >
              Try free <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" aria-labelledby="why-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="why-heading" className="section-title">Why we support startups</h2>
            <p className="section-subtitle mx-auto mt-3">Great support should not be a cost founders worry about in year one.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-3">
            {whySupport.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="rounded-3xl border border-slate-100 bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-100 text-brand-700">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20" aria-labelledby="offering-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-12 lg:grid-cols-2">
            <div>
              <h2 id="offering-heading" className="section-title">What startups get</h2>
              <p className="mt-3 text-slate-600">Everything you need to support customers, close leads, and move fast for 12 months.</p>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {offering.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.title} className="rounded-2xl border border-slate-100 bg-slate-50 p-5">
                      <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-xl bg-brand-100 text-brand-700">
                        <Icon className="h-5 w-5" />
                      </div>
                      <h3 className="font-bold text-slate-900">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-600">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-3xl border border-slate-200 bg-white p-7 shadow-xl shadow-slate-200/50">
              <h3 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                <Check className="h-5 w-5 text-green-600" /> Eligibility
              </h3>
              <ul className="mt-5 space-y-3">
                {eligibility.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-slate-700">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-6 rounded-2xl bg-slate-900 p-5 text-white">
                <p className="text-sm text-slate-300">
                  <span className="font-bold text-white">Tip:</span> Social enterprises, climate-tech, health-tech, and GovTech startups are especially encouraged to apply.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900 text-white" aria-labelledby="choose-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="choose-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">Why startups choose ZuriHub</h2>
            <p className="mx-auto mt-4 max-w-2xl text-slate-300">The same features larger teams pay for, designed to work out of the box for lean teams.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {whyChoose.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="rounded-2xl border border-slate-800 bg-slate-800/50 p-6">
                  <Icon className="h-8 w-8 text-brand-400" />
                  <h3 className="mt-4 font-bold">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-300">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white" aria-labelledby="discounts-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="discounts-heading" className="section-title">Multi-Year Startup Discounts</h2>
            <p className="section-subtitle mx-auto mt-3">Exclusive discounts for startup program members in years 2-5</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {multiYearDiscounts.map((item) => (
              <div key={item.year} className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center">
                <div className="text-4xl font-extrabold text-brand-600">{item.discount}</div>
                <div className="mt-2 font-semibold text-slate-900">{item.year}</div>
                <p className="mt-3 text-sm text-slate-600">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-50" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="mb-12 text-center">
            <h2 id="benefits-heading" className="section-title">Additional Startup Benefits</h2>
            <p className="section-subtitle mx-auto mt-3">Beyond the core platform, startups get exclusive growth and visibility benefits</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {additionalBenefits.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.title} className="rounded-3xl border border-slate-200 bg-white p-7 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                  <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-100 text-brand-700">
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-sm text-slate-600">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20" aria-labelledby="mission-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div>
              <h2 id="mission-heading" className="section-title">For startups on a mission</h2>
              <p className="mt-4 text-lg text-slate-600">
                Some founders are not just building companies — they are improving health, food, finance, education, and climate. ZuriHub helps those founders stay close to the people they serve.
              </p>
              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-3">
                  <Heart className="mt-0.5 h-5 w-5 text-rose-500" />
                  <span className="text-slate-700">Social enterprises and impact startups are fast-tracked.</span>
                </div>
                <div className="flex items-start gap-3">
                  <Calendar className="mt-0.5 h-5 w-5 text-brand-600" />
                  <span className="text-slate-700">One year of free credit so you can focus on product-market fit.</span>
                </div>
                <div className="flex items-start gap-3">
                  <Shield className="mt-0.5 h-5 w-5 text-green-600" />
                  <span className="text-slate-700">SOC 2-ready security from day one.</span>
                </div>
              </div>
            </div>
            <div className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
              <h3 className="text-lg font-bold text-slate-900 mb-6">Startups already using ZuriHub</h3>
              <div className="space-y-4">
                {startupLogos.map((s) => (
                  <div key={s.name} className="flex items-center justify-between border-b border-slate-100 pb-4 last:border-0 last:pb-0">
                    <span className="font-semibold text-slate-900">{s.name}</span>
                    <span className="text-xs text-slate-500">{s.category}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="apply" className="py-20 bg-slate-50" aria-label="Apply for startup credit">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-10 lg:grid-cols-2">
            <div>
              <h2 className="section-title">Apply for the Startup Program</h2>
              <p className="mt-3 text-slate-600">Tell us about your startup and we will review your application within 3 business days.</p>
              <div className="mt-8 space-y-4">
                {whatToExpect.map((s) => (
                  <div key={s.step} className="flex items-start gap-4 rounded-2xl border border-slate-100 bg-white p-4 shadow-sm">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand-100 text-sm font-extrabold text-brand-700">{s.step}</div>
                    <div>
                      <h4 className="font-bold text-slate-900">{s.title}</h4>
                      <p className="text-sm text-slate-600">{s.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:sticky lg:top-24">
              {submitted ? (
                <div className="rounded-3xl border border-green-200 bg-green-50 p-8 text-center shadow-sm">
                  <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
                    <Check className="h-7 w-7" />
                  </div>
                  <h3 className="mt-5 text-xl font-bold text-slate-900">Application ready</h3>
                  <p className="mt-2 text-sm text-slate-600">Your email client is open. Send the message to startup@getzurihub.com and we will get back to you within 3 business days.</p>
                  <button
                    onClick={() => {
                      setSubmitted(false);
                      setForm({ name: '', email: '', company: '', website: '', country: '', stage: '', team: '', why: '' });
                    }}
                    className="mt-5 rounded-full bg-brand-600 px-5 py-2 text-sm font-semibold text-white hover:bg-brand-700"
                  >
                    Apply again
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-xl shadow-slate-200/50">
                  <h3 className="text-xl font-bold text-slate-900">Start your application</h3>
                  <p className="mt-1 text-sm text-slate-600">Fill in your details and we will email you the next steps.</p>
                  <div className="mt-5 space-y-4">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="name" className="block text-xs font-semibold text-slate-700">Full name</label>
                        <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20">
                          <Users className="h-4 w-4 text-slate-400" />
                          <input
                            id="name"
                            type="text"
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            className="w-full bg-transparent text-sm outline-none text-slate-900 placeholder:text-slate-400"
                            placeholder="Jane Doe"
                          />
                        </div>
                      </div>
                      <div>
                        <label htmlFor="company" className="block text-xs font-semibold text-slate-700">Startup name</label>
                        <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20">
                          <Building className="h-4 w-4 text-slate-400" />
                          <input
                            id="company"
                            type="text"
                            required
                            value={form.company}
                            onChange={(e) => setForm({ ...form, company: e.target.value })}
                            className="w-full bg-transparent text-sm outline-none text-slate-900 placeholder:text-slate-400"
                            placeholder="Acme Inc."
                          />
                        </div>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-xs font-semibold text-slate-700">Work email</label>
                      <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/20">
                        <Mail className="h-4 w-4 text-slate-400" />
                        <input
                          id="email"
                          type="email"
                          required
                          value={form.email}
                          onChange={(e) => setForm({ ...form, email: e.target.value })}
                          className="w-full bg-transparent text-sm outline-none text-slate-900 placeholder:text-slate-400"
                          placeholder="founder@startup.com"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="website" className="block text-xs font-semibold text-slate-700">Website or app link</label>
                      <input
                        id="website"
                        type="url"
                        value={form.website}
                        onChange={(e) => setForm({ ...form, website: e.target.value })}
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                        placeholder="https://startup.com"
                      />
                    </div>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <label htmlFor="country" className="block text-xs font-semibold text-slate-700">Country</label>
                        <input
                          id="country"
                          type="text"
                          required
                          value={form.country}
                          onChange={(e) => setForm({ ...form, country: e.target.value })}
                          className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                          placeholder="Uganda"
                        />
                      </div>
                      <div>
                        <label htmlFor="stage" className="block text-xs font-semibold text-slate-700">Funding stage</label>
                        <select
                          id="stage"
                          value={form.stage}
                          onChange={(e) => setForm({ ...form, stage: e.target.value })}
                          className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                        >
                          <option value="">Select stage</option>
                          <option value="Pre-seed / Idea">Pre-seed / Idea</option>
                          <option value="Seed">Seed</option>
                          <option value="Series A">Series A</option>
                          <option value="Bootstrapped / Revenue-funded">Bootstrapped / Revenue-funded</option>
                          <option value="Non-profit / Social enterprise">Non-profit / Social enterprise</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                    </div>
                    <div>
                      <label htmlFor="team" className="block text-xs font-semibold text-slate-700">Team size</label>
                      <select
                        id="team"
                        value={form.team}
                        onChange={(e) => setForm({ ...form, team: e.target.value })}
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                      >
                        <option value="">Select size</option>
                        <option value="1-5">1-5</option>
                        <option value="6-15">6-15</option>
                        <option value="16-30">16-30</option>
                        <option value="31-50">31-50</option>
                        <option value="50+">50+</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="why" className="block text-xs font-semibold text-slate-700">Why ZuriHub for your startup?</label>
                      <textarea
                        id="why"
                        rows={3}
                        required
                        value={form.why}
                        onChange={(e) => setForm({ ...form, why: e.target.value })}
                        className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                        placeholder="Tell us what you are building and how ZuriHub can help..."
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-600 px-6 py-3 text-sm font-bold text-white transition hover:bg-brand-700"
                  >
                    Submit application <ArrowRight className="h-4 w-4" />
                  </button>
                  <p className="mt-3 text-center text-xs text-slate-500">By submitting, you agree to our Terms and Privacy. Application is emailed to startup@getzurihub.com.</p>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 id="faq-heading" className="text-center section-title mb-10">Startup program FAQs</h2>
          <div className="space-y-4">
            {faqs.map((f) => (
              <div key={f.q} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
                <h3 className="font-bold text-slate-900">{f.q}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900 text-white" aria-label="Get started">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold sm:text-3xl">Not ready to apply?</h2>
          <p className="mx-auto mt-3 max-w-xl text-slate-300">Start a free 14-day trial and upgrade to the startup credit once you are approved.</p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a
              href={site.workspaceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-brand-600 px-6 py-3 text-sm font-bold text-white transition hover:bg-brand-700"
            >
              Start free trial <ArrowUpRight className="h-4 w-4" />
            </a>
            <a
              href="mailto:startup@getzurihub.com"
              className="inline-flex items-center gap-2 rounded-full border border-slate-700 bg-slate-800 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
            >
              Email us <Mail className="h-4 w-4" />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
